import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  DestroyRef,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Injector,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { API, TagDTO, TagEntityDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { TagService } from '../../../services/tag.service';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { PopupAnimation, SlideInOut } from '../../../other/animations';
import { ErrorService } from '../../../services/error.service';
import { SbTagLookupsComponent } from '../sb-tag-lookups/sb-tag-lookups.component';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ColorPickerService } from '../../../services/color-picker.service';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-tag-entities',
  templateUrl: './sb-tag-entities.component.html',
  styleUrls: ['./sb-tag-entities.component.scss'],
  animations: [SlideInOut, PopupAnimation],
  standalone: false,
})
export class SbTagEntitiesComponent implements OnInit, OnDestroy {
  @ViewChild('newTagElement') newTagElement: ElementRef;
  @Input() tagEntities: TagEntityDTO[];
  @Input() dtoId: number;
  @Input() dtoType: string;
  @Input() editable: boolean;
  tagCount = input<number>(2);
  addNewTag: boolean;
  showAllTags: boolean;
  visible: boolean;
  possibleTags: TagDTO[];
  newTag: string;
  color: '#2883e9';
  showColorPicker: boolean;
  tagLoading: boolean;
  lookUpVals: TagDTO[];
  debounceTimer: ReturnType<typeof setTimeout> = null;
  lookUpString: string;
  showLookUp: boolean;
  lookUpLeft: number;
  lookUpTop: number;
  $destroySub: Subject<any> = new Subject<any>();
  @Output() tagEntitiesChange: EventEmitter<TagEntityDTO[]> = new EventEmitter<TagEntityDTO[]>();
  private listenerResize: any;
  private cmpRef: ComponentRef<SbTagLookupsComponent>;
  private viewAttachedToAppRef: boolean = false;

  constructor(
    private cdref: ChangeDetectorRef,
    private api: API,
    public util: UtilService,
    public tagService: TagService,
    public eRef: ElementRef,
    private toaster: ToasterService,
    private errorHandler: ErrorService,
    private vcRef: ViewContainerRef,
    private appRef: ApplicationRef,
    private injector: Injector,
    private cfr: ComponentFactoryResolver,
    private translate: TranslateService,
    public colorService: ColorPickerService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.showLookUp = false;
    this.tagService.tagViewOpen.pipe(takeUntil(this.$destroySub)).subscribe(openTags => {
      if (openTags > 1) {
        this.addNewTag = false;
        this.lookUpVals = null;
        this.newTag = null;
        this.tagService.tagViewOpen.next(openTags - 1);
        this.closeLookUp();
      }
    });
  }

  @HostListener('document:click', ['$event'])
  clickout(e) {
    if (!this.eRef.nativeElement.contains(e.target)) {
      this.addNewTag = false;
      this.lookUpVals = null;
      this.newTag = null;
      this.closeLookUp();
      this.tagService.tagViewOpen.next(this.tagService.tagViewOpen.getValue() - 1);
    }
  }

  getFontcolor(tag: TagDTO) {
    let fullColor = tag.color;
    if (fullColor.length <= 4) {
      const tempChr = fullColor.slice(-1);
      fullColor = fullColor + tempChr + tempChr + tempChr;
    }
    const color = this.util.convertHexToRGB(fullColor);
    return this.util.calcFontcolor(color.r, color.g, color.b);
  }

  addTagEntity() {
    if (this.newTag && this.checkForExistingTagEntities(this.newTag) === false) {
      this.checkForExistingTags(this.newTag)
        .then(possibleTag => {
          if (possibleTag) {
            this.createTagEntity(possibleTag.id, this.dtoType, this.dtoId);
          }
          this.closeLookUp();
        })
        .catch(() => {
          setTimeout(() => {
            this.showColorPicker = true;
            this.cdref.detectChanges();
            this.lookUpVals = null;
            this.closeLookUp();
          }, 250);
        });
    }
  }

  doAutocomplete(e: any | string) {
    if (e) {
      if (e.key === 'Enter') {
        if (this.permissionService.hasPermission(this.permissionService.Permission.tag_create)) {
          this.newTag = e.currentTarget.value;
          this.addTagEntity();
        } else {
          this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.globals.noPermission'));
        }
      } else if (e.key !== 'Enter') {
        this.newTag = e;
        this.addTagEntity();
      }
    }
  }

  createTag(e) {
    if (this.permissionService.hasPermission(this.permissionService.Permission.tag_create)) {
      this.showColorPicker = false;
      this.cdref.detectChanges();
      const newTag: TagDTO = new TagDTO();
      newTag.name = this.newTag;
      newTag.color = e;
      this.api
        .shared_Tag_createTag(newTag)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (tag: TagDTO) => {
            this.createTagEntity(tag.id, this.dtoType, this.dtoId);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.globals.noPermission'));
    }
  }

  cancel() {
    this.newTag = null;
    this.lookUpVals = null;
    this.openLookUp();
    this.showColorPicker = false;
    this.cdref.detectChanges();
    this.addNewTag = false;
    this.tagService.tagViewOpen.next(this.tagService.tagViewOpen.getValue() - 1);
  }

  checkForExistingTags(tagToAdd: string): Promise<TagDTO> {
    return new Promise((resolve, reject) => {
      this.api
        .shared_Tag_getTags({
          search: this.util.encodeSearchQueryParam(false, tagToAdd),
        })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (tags: TagDTO[]) => {
            this.possibleTags = tags;
            for (const possibleTag of this.possibleTags) {
              if (possibleTag.name === tagToAdd) {
                resolve(possibleTag);
                return;
              }
            }
            reject('Nothing found');
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  checkForExistingTagEntities(tagEntityToCheck: string) {
    if (this.tagEntities?.length > 0) {
      for (const tagEntity of this.tagEntities) {
        if (tagEntity.tag.name === tagEntityToCheck) {
          this.newTag = null;
          this.lookUpVals = null;
          this.addNewTag = false;
          this.toaster.toast(ToastType.INFO, 'Tag "' + tagEntityToCheck + '" already exists!');
          return true;
        }
      }
      return false;
    } else {
      return false;
    }
  }

  closeLookUp() {
    if (this.cmpRef) {
      if (this.viewAttachedToAppRef) {
        this.appRef.detachView(this.cmpRef.hostView);
      }
      this.cmpRef.destroy();
    }
    this.cmpRef = null;
    this.showLookUp = false;
  }

  lookUpForExistingTags() {
    if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
      this.api
        .shared_Tag_getTags({
          search: this.util.encodeSearchQueryParam(false, this.lookUpString),
        })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: data => {
            this.lookUpVals = data;
            this.openLookUp();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.globals.noPermission'));
    }
  }

  openLookUp() {
    if (!this.showLookUp) {
      this.showLookUp = true;
      let vcRef = this.vcRef;
      const classOfRootComponent = this.appRef.componentTypes[0];

      const appInstance = this.injector.get(classOfRootComponent, Injector.NULL);

      if (appInstance !== Injector.NULL) {
        vcRef = appInstance.vcRef || appInstance.viewContainerRef || this.vcRef;

        if (vcRef === this.vcRef) {
          console.warn("Please expose it by adding 'public vcRef: ViewContainerRef' to the constructor.");
        } else {
          this.viewAttachedToAppRef = true;
        }
      }
      const compFactory = this.cfr.resolveComponentFactory(SbTagLookupsComponent);
      if (this.viewAttachedToAppRef) {
        this.cmpRef = compFactory.create(this.injector);
        this.appRef.attachView(this.cmpRef.hostView);
        document.body.appendChild((this.cmpRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement);
        this.setLookUpContent();
        this.cmpRef.instance.itemSelected.pipe(takeUntil(this.$destroySub)).subscribe(data => {
          if (data) {
            this.doAutocomplete(data);
          }
        });
      } else {
        const injector = Injector.create({
          providers: [],
          parent: vcRef.injector,
        });

        this.cmpRef = vcRef.createComponent(compFactory, 0, injector, []);
      }

      if (this.vcRef !== vcRef) {
        this.cmpRef.changeDetectorRef.detectChanges();
      }
      this.calcPos();
    } else {
      this.calcPos();
      this.setLookUpContent();
    }
  }

  calcPos() {
    if (this.newTagElement && this.newTagElement.nativeElement) {
      const rect = this.newTagElement.nativeElement.getBoundingClientRect();
      this.lookUpLeft = rect.left - 27;
      this.lookUpTop = rect.top + document.documentElement.scrollTop + rect.height + 2;
      this.setPos();
    }
  }

  setPos() {
    if (this.cmpRef) {
      this.cmpRef.instance.left = this.lookUpLeft;
      this.cmpRef.instance.top = this.lookUpTop;
    }
  }

  setLookUpContent() {
    if (this.cmpRef) {
      this.cmpRef.instance.entries = this.lookUpVals;
      this.cmpRef.instance.searchText = this.lookUpString;
    }
  }

  createTagEntity(tagId: number, dtoType: string, dtoId: number) {
    if (this.permissionService.hasPermission(this.permissionService.Permission.tag_connect)) {
      this.api
        .shared_Tag_createTagEntity(tagId, dtoType, dtoId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: data => {
            if (!this.tagEntities) {
              this.tagEntities = [];
            }
            this.tagEntities.push(data);
            this.newTag = null;
            this.lookUpVals = null;
            this.addNewTag = false;
            this.toaster.toast(ToastType.SUCCESS, 'Tag "' + data.tag.name + '" added!');
            this.tagEntitiesChange.emit(this.tagEntities);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.globals.noPermission'));
    }
  }

  removeTagEntity(tagId: number, dtoType: string, dtoId: number): void {
    if (this.permissionService.hasPermission(this.permissionService.Permission.tag_disconnect)) {
      this.api
        .shared_Tag_deleteTagEntity(tagId, dtoType, dtoId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: () => {
            const i: number = this.tagEntities.findIndex(x => x.tag.id === tagId);
            this.tagEntities.splice(i, 1);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.globals.noPermission'));
    }
  }

  getStringForLookup(e: string) {
    this.newTag = e;
    this.lookUpString = this.newTag;
    if (this.lookUpString.length > 0) {
      this.requestDebounced();
    } else {
      this.lookUpVals = null;
    }
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.lookUpForExistingTags();
    }, 200);
  }

  addNewTagFn() {
    this.addNewTag = true;
    this.tagService.tagViewOpen.next(this.tagService.tagViewOpen.getValue() + 1);
    setTimeout(() => {
      this.newTagElement?.nativeElement?.focus();
    }, 0);
  }

  ngOnDestroy(): void {
    this.$destroySub.next(null);
    this.$destroySub.complete();
    this.closeLookUp();
  }

  ngOnInit(): void {
    this.listenerResize = () => {
      this.calcPos();
    };
    window.addEventListener('resize', this.listenerResize, { capture: true, passive: true });
  }
}
